import React, { FC, useCallback, useContext } from 'react';
import { DropzoneState } from 'react-dropzone';
import { filesize } from 'filesize';

import { Box } from '../../../Box';
import { ImageRenderer } from '../../../ImageRenderer';
import { TMediaUploaderPreviewMode } from '../../models';
import { Icon } from '@src/atoms/Icon';
import { Text } from '@src/atoms/Text';
import { IMediaImageResizeOption } from '@src/models/entity';
import { classnames } from '@src/utils/classnames';
import { getClasses } from '@src/utils/getClass';
import styled from './MediaUploaderSingleFile.scss';
import { Loader } from '@src/atoms/Loader';
import { IMediaUploaderFile } from '../../MediaUploaderController';
import { MediaUploaderContext } from '../../context';
import { Button } from '@src/atoms/Button';

interface IMediaUploaderSingleFileProps {
  file: IMediaUploaderFile;
  dropzone: DropzoneState;
  previewMode: TMediaUploaderPreviewMode;
  previewSize?: IMediaImageResizeOption;
  onLoadImage?: () => void;
  greyscale: boolean;
  /**
   * Показывать ли оригинальное имя файла и размер при загрузке
   */
  showFileInfo: boolean;
  showDeleteButton: boolean;
}

/**
 * когда загрузчик работает в режиме работы с единственным файлом.
 * Т.е. не isMulti=true
 */
export const MediaUploaderSingleFile: FC<IMediaUploaderSingleFileProps> = ({
  dropzone,
  file,
  previewMode,
  previewSize,
  greyscale,
  showFileInfo,
  onLoadImage,
  showDeleteButton,
}) => {
  const { controller } = useContext(MediaUploaderContext);
  const classes = classnames(
    styled.overlayZone,
    ...getClasses(styled, {
      previewMode,
    }),
  );
  const editOverlayClasses = classnames(
    styled.editOverlay,
    ...getClasses(styled, {
      previewMode,
    }),
  );
  const hadleRemove = () => {
    controller.setStateFiles([]);
  };
  const handleEditUpload = useCallback(() => {
    dropzone.open();
  }, [dropzone]);

  return (
    <Box className={styled.root}>
      <Box
        className={classes}
        style={{
          ...previewSize,
          position: 'relative',
        }}
      >
        {file.loading && (
          <Box className={classes} bgColor="basicGreySemiLight" justifyContent={'center'} alignItems={'center'}>
            <Loader size={28} />
          </Box>
        )}
        {!file.loading && !file.loadingError && (
          <ImageRenderer
            className={classes}
            style={{
              ...previewSize,
            }}
            url={file.previewUrl}
            greyscale={greyscale}
            onLoaded={onLoadImage}
            uploadImmediately
          />
        )}
        <div className={editOverlayClasses} onClick={handleEditUpload}>
          <input {...dropzone.getInputProps()} data-testid="dropzone" />
          <Box justifyContent={'center'} alignItems={'center'} className={styled.editOverlayInner} column>
            <Icon size={40} name={'upload'} view={'basicWhite'} />
            <Text id={'itemChange'} size={'caption'} color={'basicWhite'}>
              изменить
            </Text>
          </Box>
        </div>
      </Box>
      <Box column justifyContent={'center'}>
        {showFileInfo && file.originalName && (
          <Box marginLeft={8} column>
            <Text id={'fileOriginalName'} size={'secondary'} noOfLines={1}>
              {file.originalName}
            </Text>
            {file.fileSize && (
              <Text id={'fileSize'} size={'secondary'} noOfLines={1}>
                {`${filesize(file.fileSize, { base: 2, standard: 'jedec' })}`}
              </Text>
            )}
          </Box>
        )}
        {showDeleteButton && (
          <Box marginLeft={8}>
            <Button
              id="deleteFileBtn"
              preIcon={'trash'}
              view={'textLight'}
              onClick={hadleRemove}
              className={styled.deleteIcon}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
